// Les dates des vacances et des jours fériés, lues DEPUIS L'APPAREIL du bureau ou du responsable des salles (D-138),
// comme la FFBB (src/lib/ffbb.ts) : deux sources publiques, sans clé ni donnée personnelle —
//   * le calendrier scolaire du ministère (data.education.gouv.fr, jeu « fr-en-calendrier-scolaire »), par académie ;
//   * les jours fériés (calendrier.api.gouv.fr), par zone (métropole, Alsace-Moselle, outre-mer).
// Une lecture par semaine au plus (le lundi : un changement du ministère), la saison en cours et la suivante ; la base les
// garde par club (hall_calendar_save). Sans réponse, les dates connues restent (secours) et l'écran le dit.
// Jamais depuis un navigateur piloté (les visites guidées, les essais de mise en page : navigator.webdriver) : seule
// l'académie déduite est notée.
import { calendarSave } from './salles-api';
import { CALENDAR_SOURCE, academyOfDepartment, calendarStale, departmentOf, feriesZoneOf, parseCalendar, parseFeries } from '../domain/vacances';
import type { Week } from '../domain/semaine';

const tried = new Set<string>();
const seasonAfter = (s: string) => `${Number(s.slice(0, 4)) + 1}-${Number(s.slice(5)) + 1}`;

async function getJson(url: string): Promise<unknown> {
  const ctl = new AbortController();
  const t = window.setTimeout(() => ctl.abort(), 8000);
  try {
    const r = await fetch(url, { signal: ctl.signal });
    if (!r.ok) throw new Error(String(r.status));
    return await r.json();
  } finally { window.clearTimeout(t); }
}

/** L'académie du club : celle notée, sinon celle de son département (code FFBB, adresse). */
export function academyOfClub(w: Pick<Week, 'calendar' | 'zone'>): string | null {
  return w.calendar?.academy ?? academyOfDepartment(departmentOf({ ffbb_code: w.zone?.ffbb_code, address: w.zone?.address }));
}

/** Relit les dates si c'est le moment ; vrai si la base a changé (l'écran se recompose). Une tentative par session. */
export async function refreshCalendar(clubId: string, w: Week): Promise<boolean> {
  if (!w.manage) return false;
  const academy = academyOfClub(w);
  if (!academy || tried.has(clubId)) return false;
  tried.add(clubId);
  const dep = w.zone?.department ?? departmentOf({ ffbb_code: w.zone?.ffbb_code, address: w.zone?.address });
  const zone = w.calendar?.feries_zone ?? feriesZoneOf(dep);
  const automated = typeof navigator !== 'undefined' && (navigator as Navigator & { webdriver?: boolean }).webdriver === true;
  if (automated || !calendarStale({ read_at: w.calendar?.read_at ?? null, read_academy: w.calendar?.read_academy ?? null }, academy, new Date())) {
    // Rien à lire : l'académie déduite est notée une fois (les dates de secours de sa zone en découlent).
    if (!w.calendar?.academy) { await calendarSave(clubId, academy, null, null, null).catch(() => null); return true; }
    return false;
  }
  const seasons = [w.season, seasonAfter(w.season)];
  const years = [Number(w.season.slice(0, 4)), Number(w.season.slice(5)), Number(w.season.slice(5)) + 1];
  const [vac, fer] = await Promise.all([
    getJson(CALENDAR_SOURCE.vacances(academy, seasons)).then(j => parseCalendar(((j as { results?: unknown[] })?.results ?? []) as never[], academy, seasons)).catch(() => null),
    Promise.all(years.map(y => getJson(CALENDAR_SOURCE.feries(zone, y)).then(parseFeries))).then(x => x.flat()).catch(() => null),
  ]);
  const inSeasons = (d: string) => d >= `${w.season.slice(0, 4)}-07-01` && d < `${Number(w.season.slice(5)) + 1}-07-01`;
  const holidays = vac?.length ? vac : null;
  const feries = fer?.length ? fer.filter(f => inSeasons(f.day)) : null;
  if (!holidays && !feries) {
    if (!w.calendar?.academy) { await calendarSave(clubId, academy, null, null, null).catch(() => null); return true; }
    return false;
  }
  return !!(await calendarSave(clubId, academy, holidays, feries, zone).catch(() => null));
}
